import { useEffect, useRef, useState } from "react";
import type { VilmerActivitiesResult } from "@shared/types/foyer";
import { getBinding } from "../../lib/binding";
import { DEMO_BRAND, demoActivities } from "@shared/vilmer-demo";

/** How often a TV re-asks for its activity list. The server holds its own,
 *  longer, upstream TTL - this only decides how quickly a cancellation entered
 *  by staff at 09:00 reaches a wall, without needing a reboot. */
const POLL_MS = 10 * 60 * 1000;

export interface VilmerState {
  result: VilmerActivitiesResult | null;
  /** True until the first answer lands, so the pane holds its breath instead of
   *  flashing "inga aktiviteter" and then filling in. */
  loading: boolean;
  /** The fetch failed and we have nothing at all to show. */
  failed: boolean;
}

/**
 * Fetch this room's VilMer activities.
 *
 * The TV sends neither the API URL nor the room's token - it names its own
 * premise and room and the server resolves both (see src/server/api/vilmer.ts).
 * `previewPremiseId`/`previewRoomId` cover the CMS live preview, which has a
 * session but no device binding.
 *
 * `from`/`to` are LOCAL `YYYY-MM-DD` dates, because an activity is a wall-clock
 * event at the premise, not an instant. They are also what keys the server's
 * cache, so the caller should pass a stable window (a whole ISO week) rather
 * than a rolling one - otherwise every TV in the building asks for a slightly
 * different range and the cache never hits.
 */
export function useVilmerActivities(
  enabled: boolean,
  range: { from: string; to: string },
  previewPremiseId?: string,
  previewRoomId?: string
): VilmerState {
  const [result, setResult] = useState<VilmerActivitiesResult | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  // Read inside the fetch without making it a dependency - including `result`
  // would tear down and rebuild the poll on every successful refresh.
  const hasResult = useRef(false);

  const { from, to } = range;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const binding = getBinding();
    const premiseId = binding?.premiseId ?? previewPremiseId;
    const roomId = binding?.roomId ?? previewRoomId;
    if (!premiseId || !roomId) {
      // The `?scheme=care` design preview: no premise, no room, no backend to
      // ask. Serve the same stand-in programme the server would, so the pane
      // can actually be looked at. A preview that shows an empty week is a
      // preview of the empty state, not of the screen.
      setResult({
        brand: DEMO_BRAND,
        activities: demoActivities("preview", from, to),
        fetchedAt: new Date().toISOString(),
      });
      hasResult.current = true;
      setLoading(false);
      return;
    }

    let alive = true;
    const headers: Record<string, string> = {};
    if (binding?.screenToken) headers["X-Screen-Token"] = binding.screenToken;

    const load = async () => {
      try {
        const res = await fetch(
          `/api/foyer/premise/${encodeURIComponent(premiseId)}` +
            `/room/${encodeURIComponent(roomId)}/activities` +
            `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
          { headers }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as VilmerActivitiesResult;
        if (!alive) return;
        hasResult.current = true;
        setResult(json);
        setFailed(false);
      } catch {
        if (!alive) return;
        // Keep whatever is already on screen - this morning's programme is
        // still today's programme. Only report failure when we have nothing.
        if (!hasResult.current) setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    };

    void load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [enabled, from, to, previewPremiseId, previewRoomId]);

  return { result, loading, failed };
}
